// Đổi lỗi API của trang Studio sang câu tiếng Việt (SPEC Studio mục 8.6, 8.7).
// Interceptor axios ném `error.response.data` nên `err` = { statusCode, message, error? }; mất mạng thì
// `err` = { message: 'Không kết nối được máy chủ.', networkError: true } (utils/axios-utils.ts).
// Backend Studio trả { statusCode, message: 'MÃ', error: 'câu tiếng Việt' } và KHÔNG dùng 401/403.

import { mapStarError } from 'pages/star-shared/star-error';
import {
  GENERIC_ERROR_TEXT,
  NETWORK_ERROR_TEXT,
  STUDIO_ERROR_TEXT,
  STUDIO_UNAVAILABLE_TEXT,
} from './labels';

// Chữ chung của HTTP / mã trơn ở `error` không phải câu để hiện.
const GENERIC_HTTP =
  /^(Bad Request|Not Found|Conflict|Unprocessable Entity|Forbidden|Unauthorized|Internal Server Error|Service Unavailable|Too Many Requests)$/i;
const CODE_LIKE = /^[A-Z][A-Z0-9_]{3,}$/;
const KNOWN_PREFIX = /^(STUDIO_|KC_|APPROVAL_|STAR_)/;

// C11 (docs/studio-quan-ly/03-chot-sau-dot-1.md): backend bản cũ chưa có /api/studios/* → NestJS trả
// 404 { message: 'Cannot GET /api/studios/…', error: 'Not Found' }. Lỗi 404 có mã (STUDIO_NOT_FOUND…) không
// rơi vào đây vì `message` của chúng là mã.
export const STUDIO_ROUTE_MISSING_TEXT = 'Chức năng Studio chưa sẵn sàng trên máy chủ này.';
const MISSING_STUDIO_ROUTE = /^Cannot (GET|POST|PUT|PATCH|DELETE) \S*\/studios(\/|\?|$)/;

/** 404 vì máy chủ chưa có route /studios (không phải 404 nghiệp vụ). */
export function isStudioRouteMissing(err: any): boolean {
  const status = Number(err?.statusCode ?? err?.status);
  return (
    status === 404 && typeof err?.message === 'string' && MISSING_STUDIO_ROUTE.test(err.message)
  );
}

/** Mã lỗi nghiệp vụ (`message` dạng MÃ_IN_HOA), hoặc null. */
export function studioErrorCode(err: any): string | null {
  const raw = err?.message;
  return typeof raw === 'string' && CODE_LIKE.test(raw) ? raw : null;
}

export function hasStudioErrorCode(err: any, code: string): boolean {
  return studioErrorCode(err) === code;
}

/**
 * Thứ tự (mục 8.6): câu tiếng Việt ở `err.error` → bảng mã trong labels.ts (STUDIO_*, KC_*, APPROVAL_*,
 * STAR_*) → `message` là mảng (class-validator) thì nối bằng dấu phẩy → mất mạng → câu chung.
 */
export function mapStudioError(err: any): string {
  const errorText = typeof err?.error === 'string' ? err.error.trim() : '';
  if (errorText && !GENERIC_HTTP.test(errorText) && !CODE_LIKE.test(errorText)) return errorText;
  if (isStudioRouteMissing(err)) return STUDIO_ROUTE_MISSING_TEXT;

  const code = studioErrorCode(err);
  if (code && KNOWN_PREFIX.test(code)) {
    if (STUDIO_ERROR_TEXT[code]) return STUDIO_ERROR_TEXT[code];
    if (code.startsWith('STAR_')) return mapStarError(err);
  }
  if (Array.isArray(err?.message)) {
    const parts = err.message.filter((part: unknown) => typeof part === 'string' && part);
    if (parts.length) return parts.join(', ');
  }
  if (err?.networkError) return NETWORK_ERROR_TEXT;
  return GENERIC_ERROR_TEXT;
}

/** Mã lỗi cấp trang hiện thành trang 403 (mục 8.7). */
export const PAGE_FORBIDDEN_CODES = ['STUDIO_SCOPE', 'STUDIO_NOT_MANAGER', 'STUDIO_ROLE_NOT_ALLOWED'];

export type StudioErrorKind = 'forbidden' | 'unavailable' | 'error';

export interface StudioErrorView {
  /**
   * forbidden → `Result status="403"` với `message`, nút về /studio hoặc /;
   * unavailable → `Result status="warning"` với `message`;
   * error → `Alert type="error"` với `message` + nút "Thử lại".
   */
  kind: StudioErrorKind;
  code: string | null;
  message: string;
}

/** Cách hiện một lỗi tải dữ liệu ở cấp trang / tab (bảng trạng thái mục 8.7). */
export function describeStudioError(err: any): StudioErrorView {
  const code = studioErrorCode(err);
  const status = Number(err?.statusCode ?? err?.status);
  if (code === 'STUDIO_UNAVAILABLE' || (status === 503 && !code)) {
    return { kind: 'unavailable', code, message: STUDIO_UNAVAILABLE_TEXT };
  }
  if (code && PAGE_FORBIDDEN_CODES.includes(code)) {
    return { kind: 'forbidden', code, message: mapStudioError(err) };
  }
  return { kind: 'error', code, message: mapStudioError(err) };
}
